<template>
  <div class="VB_Brochure" v-if="!loading && pagesLoaded && logoLoaded">
    <div v-if="mountTimer">
      <ViewmedicaBrochurePage
        v-for="page in pages"
        :key="page"
        :page="page"
        :image="pageArt[page - 1]"
        :logo="logo"
      />
    </div>

    <ViewmedicaFloatingPrintIndicator />
  </div>
</template>

<script>
import axios from "axios";
import { mapGetters, mapState } from "vuex";
import ViewmedicaBrochurePage from "@/components/ViewmedicaBrochurePage";
import ViewmedicaFloatingPrintIndicator from "@/components/ViewmedicaFloatingPrintIndicator.vue";

export default {
  name: "ViewmedicaBrochureIndex",

  components: {
    ViewmedicaBrochurePage,
    ViewmedicaFloatingPrintIndicator
  },

  watch: {
    pageTitle(val) {
      if (val) {
        document.title = val;
      }
    }
  },

  computed: {
    ...mapGetters({
      pages: "pages",
      pageTitle: "pageTitle"
    }),

    ...mapState({
      loading: s => s.loading
    }),

    language() {
      return this.$route.params.language;
    },

    branding: {
      get() {
        return this.$store.state.branding;
      },

      set(val) {
        this.$store.commit("BRANDING", val);
      }
    }
  },

  created() {
    this.$Progress.start();
  },

  mounted() {
    this.handleScaling();
    this.fetch();
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.handleResized);
  },

  data() {
    return {
      logo: null,
      pageArt: [],
      mountTimer: false,
      pagesLoaded: false,
      logoLoaded: false
    };
  },

  methods: {
    async fetch() {
      console.log("Brochure -> fetch");
      try {
        await this.$store.dispatch("fetch");
        await this.requestBranding();
        await this.requestArt();
        await this.requestLogo();
        setTimeout(() => {
          this.authenticate();
          this.$Progress.finish();
          this.mountTimer = true;
        }, 300);
      } catch (err) {
        console.error(`Error: ${err}`);
        this.$Progress.fail();
        let errCode = isNaN(parseInt(err)) ? 500 : parseInt(err);
        this.$router.replace({ name: `error-${errCode}` });
      }
    },

    handleScaling() {
      this.handleResized();
      window.addEventListener("resize", this.handleResized);
    },

    handleResized() {
      this.$store.dispatch("setScale");
    },

    authenticate() {
      if (this.$store.getters.authenticated) return true;
      return this.$router.replace({ name: "error-403" });
    },

    requestArt() {
      return new Promise((resolve, reject) => {
        console.log(`Brochure -> request art ${this.pages}`);

        const imageRequests = Array.from(
          { length: this.pages },
          (v, i) => i + 1
        ).map(this.requestPageArt);

        Promise.all(imageRequests)
          .then(values => {
            this.pageArt = values;
            this.pagesLoaded = true;
            resolve(values);
          })
          .catch(err => {
            reject(err);
          });
      });
    },

    requestBranding() {
      return new Promise((resolve, reject) => {
        const base = process.env.VUE_APP_BRANDING_BASE_URL;
        axios
          .get(`${base}${this.$route.params.client}/preferences`, {
            headers: {
              "x-viewmedica-embedded-domain": process.env.VUE_APP_EMBEDDED_DOMAIN
            }
          })
          .then(response => {
            this.branding = response.data;
            resolve(response.data);
          })
          .catch(e => {
            reject(e);
          });
      });
    },

    requestLogo() {
      return new Promise((resolve, reject) => {
        if (!this.branding.logo) {
          this.logoLoaded = true;
          return resolve(null);
        }
        console.log(`Brochure -> request logo`);
        const base = process.env.VUE_APP_MEDIA_BASE_URL;
        const img = new Image();
        img.onload = () => {
          this.logo = img;
          this.logoLoaded = true;
          resolve(img);
        };
        img.onerror = () => {
          reject(500);
        };
        if (this.$route.params.client === "5455") {
          img.src = "/brands/gwn.svg";
        } else if (this.branding.logo.search("http") > -1) {
          img.src = this.branding.logo;
        } else {
          img.src = `${base}${this.branding.logo}`;
        }
      });
    },

    requestPageArt(page) {
      return new Promise((resolve, reject) => {
        console.log(`Brochure -> request page ${page}`);
        const img = new Image();
        img.onload = () => {
          console.log(`Brochure -> page ${page} completed`);
          resolve(img);
        };
        img.onerror = () => {
          console.error(`Brochure -> page ${page} error`);
          if (this.$store.getters.development) {
            return resolve();
          }
          reject(500);
        };
        const base = process.env.VUE_APP_VERSION_BASE_URL;
        img.src = `${base}${this.$route.params.client}/graphics/${this.$store.getters.file}/${page}?language=${this.language}${this.$store.getters.development}`;
      });
    }
  }
};
</script>

<style lang="scss">
.VB_Brochure {
  height: 100%;
  margin: 0;
  padding: 0;
  background: rgb(83, 86, 89);
  display: flex;
  justify-content: center;

  @media print {
    transform: none;
    background: transparent;
  }
}
</style>
